import { useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { Sidebar, type NavKey } from '@/components/organisms/Sidebar';
import { useAuthStore } from '@/lib/stores/auth';
import { useReauthStore } from '@/lib/stores/reauth';
import { setLogoutCallback, setReauthCallback } from '@/lib/api';
import { setGleapLauncherVisible } from '@/lib/gleap';
import { useProfileQuery } from '@/features/settings/queries';
import { applyTheme } from '@/lib/theme';
import { OnboardingProvider } from '@/features/onboarding/OnboardingProvider';
import { OnboardingModal } from '@/features/onboarding/OnboardingModal';
import { ReAuthModal } from '@/features/auth/ReAuthModal';
import { BillingAlertBanner } from '@/features/billing/BillingAlertBanner';
import { UsageAlertBanner } from '@/features/billing/UsageAlertBanner';
import { CommandPalette } from '@/features/command-palette/CommandPalette';
import { RequiredNameDialog } from '@/features/settings/profile/RequiredNameDialog';

export type AppNavKey = NavKey;

const UNCHROMED_EXACT = new Set(['/login', '/register']);
const UNCHROMED_PREFIXES = ['/auth/'];

function isUnchromedRoute(path: string): boolean {
  if (UNCHROMED_EXACT.has(path)) return true;
  return UNCHROMED_PREFIXES.some((p) => path.startsWith(p));
}

function pathToNavKey(path: string): NavKey | undefined {
  // `/` is kept in this check defensively — in practice `beforeLoad` on the
  // index route redirects away before the shell sees `/`, but if a navigation
  // is in flight we still want the Email Accounts nav item highlighted.
  if (path === '/' || path.startsWith('/email-accounts')) return 'accounts';
  if (path.startsWith('/action-items')) return 'action-items';
  if (path.startsWith('/templates')) return 'templates';
  if (path.startsWith('/notifications')) return 'notifications';
  if (path.startsWith('/audit-logs')) return 'audit-logs';
  if (path.startsWith('/billing')) return 'billing';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/api-keys')) return 'api-keys';
  if (path.startsWith('/partner-integration')) return 'partner-integration';
  return undefined;
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const showChrome = !isUnchromedRoute(pathname);

  // Auth state comes from the Zustand store, which is hydrated from
  // localStorage by its persist middleware. The selectors keep this component
  // subscribed to just the slices it renders.
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const userName = user?.name ?? '';
  const userEmail = user?.email ?? '';

  // Apply the user's saved color scheme app-wide. The inline script in
  // index.html already painted localStorage's value pre-mount; this reconciles
  // to the server's value (the cross-device source of truth) once the profile
  // loads — a one-time correction on a fresh browser. Gated on auth so the
  // query doesn't fire 401s on the public login/register routes.
  const theme = useProfileQuery({ enabled: !!accessToken }).data?.theme;
  useEffect(() => {
    if (theme) applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (!showChrome) return;
    setLogoutCallback(() => {
      void navigate({ to: '/login', replace: true });
    });
    // On a terminal 401, open the in-place re-auth modal instead of redirecting
    // (TRU-301). The token is intentionally left in storage, so the `!accessToken`
    // redirect effect below does not fire.
    setReauthCallback(() => useReauthStore.getState().open());
    return () => {
      setLogoutCallback(() => {});
      setReauthCallback(() => {});
    };
  }, [navigate, showChrome]);

  useEffect(() => {
    if (!showChrome) return;
    if (!accessToken) {
      void navigate({ to: '/login', replace: true });
    }
  }, [navigate, showChrome, accessToken]);

  // Support chat is opened from the sidebar's Help button, so the pages
  // without the sidebar show Gleap's floating launcher instead.
  useEffect(() => {
    setGleapLauncherVisible(!showChrome);
  }, [showChrome]);

  if (!showChrome) return <>{children}</>;

  const active = pathToNavKey(pathname);

  return (
    <OnboardingProvider>
      <div
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          background: 'var(--shell)',
          overflow: 'hidden',
        }}
      >
        <Sidebar active={active} userName={userName} userEmail={userEmail} />
        <main
          style={{
            flex: 1,
            minWidth: 0,
            margin: '8px 8px 8px 8px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // Match the navbar/sidebar background (transparent over --shell) so
            // the route panel dissolves into the app shell — fully flat, seamless.
            background: 'var(--shell)',
          }}
        >
          {/* App-wide billing banner — above the route panel so it's the first
              thing on every authed page, and inside <main> so it sits within the
              shell's gutters rather than fighting the sidebar. Gated on a token
              because the unauthed frames of this shell (pre-redirect) must not
              fire an authed billing read. Renders null unless the summary
              carries a banner, so it costs nothing the other 99% of the time. */}
          {accessToken && <BillingAlertBanner className="mb-2" />}
          {/* TRU-540 usage banner — STACKED under the billing banner, not merged
              into it. The two answer different questions ("is your subscription
              healthy?" vs "are you running out of room?") and an org can be in
              trouble on both at once, so folding this into `deriveBanner`'s
              precedence chain would hide "you're at 95% of your mailboxes"
              behind a PAST_DUE notice — the case where hitting a hard limit is
              most surprising. Each reads its own field off the same summary and
              renders null independently. */}
          {accessToken && <UsageAlertBanner className="mb-2" />}
          {/* Required for a signed-in user with no name (Saleshandy owners can
              arrive without one). Renders nothing once the name is set. */}
          {accessToken && <RequiredNameDialog />}
          {children}
        </main>
        <OnboardingModal />
        <ReAuthModal />
        <CommandPalette />
      </div>
    </OnboardingProvider>
  );
}
